import { useMemo } from 'react';
import type { CheckerValue } from '~/components/StampCard/CheckerSelector';
import type { ActionValue } from '~/components/StampCard/ActionSelector';
import type { StampingRule } from '~/components/StampCard/StampingRulesManager';
import type {
  CheckerSchema,
  ActionSchema,
} from '~/resources/loyalty/stamp-template/hooks/useStampingSchemas';
import { useTranslate } from '@refinedev/core';

interface UseStampingPreviewsProps {
  availableCheckers?: CheckerSchema[];
  availableActions?: ActionSchema[];
}

export const useStampingPreviews = ({
  availableCheckers = [],
  availableActions = [],
}: UseStampingPreviewsProps = {}) => {
  const t = useTranslate();

  const getCheckerPreview = useMemo(
    () =>
      (checker: CheckerValue, checkerSchema?: CheckerSchema): string => {
        if (!checker?.code || !checker?.params) return '';

        const schema = checkerSchema || availableCheckers.find((c) => c.code === checker.code);

        switch (checker.code) {
          case 'order-amount': {
            const amount = (checker.params.amount || 0) / 100;
            const amountWithCurrency = amount.toLocaleString('en-MY', {
              style: 'currency',
              currency: 'MYR',
            });

            const field = checker.params.field || 'amount';
            const operator = checker.params.operator || '>';

            // Build the string without passing operator through translation
            // to avoid HTML encoding of special characters
            const translatedParts = t('stampTemplates.stamping.checker.orderAmount', {
              field: field,
              amount: amountWithCurrency,
              fallback: `Order {{field}} {{operator}} {{amount}}`,
            });

            // Replace the operator placeholder manually to avoid HTML encoding
            return translatedParts.replace('{{operator}}', operator);
          }

          case 'always-true':
            return t('stampTemplates.stamping.checker.alwaysTrue', {
              fallback: 'Always eligible',
            });

          case 'contain-referral':
            return t('stampTemplates.stamping.checker.containReferral', {
              minReferrals: checker.params.minReferrals || 1,
              fallback: `Contains ${checker.params.minReferrals || 1} or more referrals`,
            });

          case 'service-usage':
            return t('stampTemplates.stamping.checker.serviceUsage', {
              fallback: 'customer participates on the participating services',
            });

          case 'customer-tier': {
            const operator = checker.params.operator || '>=';
            const tier = checker.params.tier || 'bronze';

            const translatedParts = t('stampTemplates.stamping.checker.customerTier', {
              tier: tier,
              fallback: `Customer tier {{operator}} {{tier}}`,
            });

            return translatedParts.replace('{{operator}}', operator);
          }

          default:
            return schema?.name || checker.code;
        }
      },
    [availableCheckers, t]
  );

  const getActionPreview = useMemo(
    () =>
      (
        action: ActionValue,
        actionSchema?: ActionSchema,
        checkers?: CheckerValue[] // NEW: Optional checkers for context
      ): string => {
        if (!action?.code || !action?.params) return '';

        const schema = actionSchema || availableActions.find((a) => a.code === action.code);
        const stamps = action.params.stamps;

        // Determine recipient based on checker context
        const hasReferralChecker = checkers?.some(
          (c) => c.code === 'referral-amount' || c.code === 'contain-referral'
        );

        switch (action.code) {
          case 'manual-stamps':
            // Context-aware description based on rule type
            if (hasReferralChecker) {
              return t('stampTemplates.stamping.action.manualStampsReferrer', {
                count: stamps || 0,
                fallback: `Award ${stamps || 0} ${stamps === 1 ? 'stamp' : 'stamps'} to referrer`,
              });
            }
            return t('stampTemplates.stamping.action.manualStampsCustomer', {
              count: stamps || 0,
              fallback: `Award ${stamps || 0} ${stamps === 1 ? 'stamp' : 'stamps'} to customer`,
            });

          case 'order': {
            const count = stamps || 0;
            const field = action.params.field || 'field';
            const operator = action.params.operator || '=';
            const value = action.params.value || 'value';

            const translatedParts = t('stampTemplates.stamping.action.order', {
              count: count,
              field: field,
              value: value,
              fallback: `Award ${count} ${count === 1 ? 'stamp' : 'stamps'} when {{field}} {{operator}} {{value}}`,
            });

            // Replace operator manually to avoid HTML encoding
            return translatedParts.replace('{{operator}}', operator);
          }

          case 'referral': {
            const perRef = action.params.stampsPerReferral || 0;
            const maxLimit = action.params.maxStamps;

            if (maxLimit) {
              return t('stampTemplates.stamping.action.referralWithMax', {
                count: perRef,
                max: maxLimit,
                fallback: `Award ${perRef} ${
                  perRef === 1 ? 'stamp' : 'stamps'
                } per referral (max: ${maxLimit})`,
              });
            }

            return t('stampTemplates.stamping.action.referral', {
              count: perRef,
              fallback: `Award ${perRef} ${perRef === 1 ? 'stamp' : 'stamps'} per referral`,
            });
          }

          case 'percentage': {
            const percentage = action.params.percentage || 0;
            const divisor = action.params.divisor || 10;
            const maxStamps = action.params.maxStamps;

            if (maxStamps) {
              return t('stampTemplates.stamping.action.percentageWithMax', {
                divisor,
                percentage,
                max: maxStamps,
                fallback: `Award 1 stamp per $${divisor} (${percentage}% rate) (max: ${maxStamps})`,
              });
            }

            return t('stampTemplates.stamping.action.percentage', {
              divisor,
              percentage,
              fallback: `Award 1 stamp per $${divisor} (${percentage}% rate)`,
            });
          }

          default:
            return schema?.name || action.code;
        }
      },
    [availableActions, t]
  );

  const getRulePreview = useMemo(
    () =>
      (rule: StampingRule): string => {
        const checkers = Array.isArray(rule.checkers) ? rule.checkers : [];
        const hasValidCheckers = checkers.length > 0 && checkers.some((c) => c?.code);
        const hasValidAction = rule.action?.code;

        if (!hasValidCheckers || !hasValidAction) {
          return t('stampTemplates.stamping.rule.incomplete', {
            fallback: 'Incomplete rule configuration',
          });
        }

        const checkerPreviews = checkers.filter((c) => c?.code).map((c) => getCheckerPreview(c));

        const conditionsText =
          checkerPreviews.length > 0
            ? checkerPreviews.join(` ${t('common.and', { fallback: 'AND' })} `)
            : t('stampTemplates.stamping.rule.conditions', { fallback: 'conditions' });

        // Pass checkers for context-aware action preview
        const actionText = getActionPreview(rule.action, undefined, checkers);

        return t('stampTemplates.stamping.rule.preview', {
          conditions: conditionsText,
          action: actionText,
          fallback: `IF ${conditionsText} THEN ${actionText}`,
        });
      },
    [getCheckerPreview, getActionPreview, t]
  );

  const getRuleSummary = useMemo(
    () =>
      (rules: StampingRule[]): string => {
        if (!rules || rules.length === 0) {
          return t('stampTemplates.stamping.summary.noRules', {
            fallback: 'No rules configured',
          });
        }

        const validRules = rules.filter(
          (r) => r.checkers?.length > 0 && r.checkers.some((c) => c?.code) && r.action?.code
        );

        if (validRules.length === 0) {
          return t('stampTemplates.stamping.summary.noValidRules', {
            fallback: 'No valid rules configured',
          });
        }

        if (validRules.length === 1) return getRulePreview(validRules[0]);

        return t('stampTemplates.stamping.summary.multipleRules', {
          count: validRules.length,
          fallback: `${validRules.length} stamping rules configured`,
        });
      },
    [getRulePreview, t]
  );

  return {
    getCheckerPreview,
    getActionPreview,
    getRulePreview,
    getRuleSummary,
  };
};
